import { Alert, Button, Clickable, Col, Row, Spinner2, Text } from 'components';
import { Api, DURATION_FIELDS, hasParts, msToParts, partsToMs, theme } from 'lib';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ClaimOrderEditor from './ClaimOrderEditor';
import { DEFAULT_ORDER, orderPayload, readOrder, validateOrder } from './pollQuizPrecedence';
import Section from './Section';

/**
 * A large typed value used to visually collide with the native up/down spinner this box is too
 * narrow to host — the digits and the spinner arrows overlapped into a garbled mess. The spinner
 * serves no purpose at this size anyway (nobody increments a day count by clicking an arrow), so
 * it's suppressed rather than made room for.
 */
const DurationDigitInput = styled.input`
  width: 72px;
  padding: 6px 6px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid ${theme.GREY_20 || '#eee'};

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

/**
 * The global Poll / Quick Quiz precedence editor.
 *
 * The rotation used to be hardcoded in the backend, so changing "who appears when" was a deploy.
 * This is the screen that ends that.
 *
 * ONE ranked list, and deliberately not a timeline. The four options are a precedence order evaluated
 * at a single instant — the server checks them in this order and the first one that is actually
 * available right now wins the shared slot. Laying them along a kickoff-to-full-time rail, which two
 * earlier versions of this screen did, asserts a sequence in TIME that does not exist: putting the
 * quiz first does not schedule it, it just means "prefer the quiz when there is a playable one".
 *
 * The backend used to store this order per game phase, and this screen flattened it for display and
 * fanned it back out on save. That axis is gone now — nothing ever needed a phase to differ — so the
 * one order the editor shows is also the one thing stored, and the flattening machinery went with it.
 *
 * All pure logic lives in pollQuizPrecedence.js and is unit-tested there; this component only holds
 * state, renders, and calls the API.
 */

/**
 * The two timings an admin may change — one per quiz type, which is what makes them tabs.
 *
 * They are the same question ("how long does this quiz stay on screen after it stops being
 * playable?") asked of the two kinds of quiz, and only ever one at a time. Showing both at once
 * invited reading them as a pair of unrelated knobs, and each label had to carry the name of the
 * quiz it belonged to; the tab says that now, so the labels can be short.
 *
 * Six other timings the platform runs on were removed from this form, not hidden from it: they
 * describe how a match is READ (the half-time break length, kickoff-offset fallbacks for missing live
 * data, the assumed match length, current-match lookbacks) rather than what the product wants from
 * it, and a wrong value there fails silently in a way no admin can diagnose. They are fixed
 * constants in the backend now.
 *
 * `key` is the field on the config document; `tab` is what the admin picks. `defaultMs` mirrors
 * `DEFAULT_POLL_QUIZ_TIMINGS` in the backend's constants/pollQuizConfig.ts — keep the two in
 * step, the same way DEFAULT_ORDER below has to stay in step with DEFAULT_CLAIM_ORDER.
 */
const QUIZ_TABS = [
  {
    key: 'dailyQuizResultsWindowMs',
    tab: 'Daily Quiz',
    label: 'Results stay up for',
    desc: 'How long a non-gameday Quick Quiz keeps showing its results after it closes.',
    defaultMs: 24 * 60 * 60 * 1000
  },
  {
    key: 'quizPostMatchVisibilityMs',
    tab: 'Half-time Quiz',
    label: 'Stays visible after full time for',
    desc: 'How long a Game Day quiz keeps its place after the final whistle.',
    defaultMs: 48 * 60 * 60 * 1000
  }
];

const card = {
  padding: '12px 14px',
  borderRadius: '10px',
  border: `1px solid ${theme.GREY_20 || '#eee'}`,
  backgroundColor: '#fff',
  width: '100%'
};

const PollQuizConfigTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  /**
   * Timings held as { key: { days, hours, minutes } }, not as milliseconds.
   *
   * The three fields are the source of truth while editing, and milliseconds are derived once on
   * save. Converting on every keystroke instead would normalise under the cursor: typing "25" into
   * hours would become 1 day 1 hour mid-keystroke, and the field the admin was typing in would
   * silently rewrite itself to "1".
   */
  const [timingParts, setTimingParts] = useState({});
  const [activeTab, setActiveTab] = useState(QUIZ_TABS[0].key);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [timingsError, setTimingsError] = useState(null);
  /**
   * The last-known-saved shape of `order`/`timingParts`, snapshotted on every successful fetch
   * (including the one `handleSave` triggers after a save). Compared against the live values to
   * decide whether Save Changes has anything left to save.
   */
  const [baseline, setBaseline] = useState({ order: DEFAULT_ORDER, timingParts: {} });

  const active = QUIZ_TABS.find((t) => t.key === activeTab) ?? QUIZ_TABS[0];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await Api.get('/admin/pollQuizConfig/get');
      if (res?.data) {
        // A config document written before the backend collapsed its per-phase table comes back with
        // no claimOrder at all; readOrder falls back to the default, which is what the server itself
        // is using in that case.
        const nextOrder = readOrder(res.data.claimOrder);
        const stored = res.data.timings ?? {};
        // The fields as they were typed (48 hours stays 48 hours) where the server has them. It only
        // returns an entry while it still adds up to the timing in force, so anything else — never
        // saved from this form, or replaced by a default — falls back to splitting the milliseconds.
        const typed = res.data.timingsInput ?? {};
        const nextTimingParts = Object.fromEntries(
          QUIZ_TABS.map((row) => [row.key, typed[row.key] ?? msToParts(stored[row.key])])
        );

        setOrder(nextOrder);
        setTimingParts(nextTimingParts);
        // This is the only place the baseline moves — including after a save, since handleSave
        // calls fetchConfig() again on success, so the just-saved values become the new baseline.
        setBaseline({ order: nextOrder, timingParts: nextTimingParts });
      }
    } catch (error) {
      console.error('Error fetching poll/quiz configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const payload = useMemo(() => orderPayload(order), [order]);
  // A belt-and-braces mirror of the endpoint's own rules. Every problem it can report is now
  // structurally unreachable from this UI, so anything it finds is a bug here rather than admin
  // error — but Save must not send an order the server will reject either way.
  const problems = useMemo(() => validateOrder(payload.claimOrder), [payload]);
  /**
   * Whether the live form actually differs from `baseline`. Timing fields are compared through
   * partsToMs rather than field-by-field, because a field the admin has typed into holds a string
   * (`e.target.value`) while the same field fresh off `msToParts` holds a number — comparing the
   * raw parts would read "2" vs 2 as a change that isn't there. partsToMs normalises both sides to
   * the same millisecond value before they're compared.
   */
  const isDirty = useMemo(() => {
    const orderChanged =
      order.length !== baseline.order.length ||
      order.some((claim, i) => claim !== baseline.order[i]);
    const timingsChanged = QUIZ_TABS.some(
      (row) => partsToMs(timingParts[row.key]) !== partsToMs(baseline.timingParts[row.key])
    );
    return orderChanged || timingsChanged;
  }, [order, timingParts, baseline]);

  /**
   * Takes explicit `orderToSave`/`timingPartsToSave` arguments rather than reading `order`/
   * `timingParts` off the closure, so a caller that just changed those via setOrder/setTimingParts
   * in the same handler (the reset confirmation) can save the NEW values immediately — those
   * setters don't take effect until the next render, so reading the state variables here would
   * still see the pre-reset values.
   */
  const persistConfig = async (orderToSave, timingPartsToSave) => {
    if (validateOrder(orderPayload(orderToSave).claimOrder).length) return;
    try {
      setSaving(true);
      /**
       * Only the timings the admin actually filled in.
       *
       * This used to send both keys unconditionally, as `partsToMs(timingParts[key])` — and
       * `partsToMs` maps blank fields to 0. So a config with no stored timings loaded as blank
       * boxes, and saving this screen for ANY reason (reordering the precedence tree, most likely)
       * wrote `quizPostMatchVisibilityMs: 0`. That is not a short window: at zero the backend drops
       * every Game Day quiz from the Team Channel and from the Play to Win list the instant its
       * match reads as ended, so one save disabled the feature platform-wide with nothing on screen
       * to say so.
       *
       * An omitted key leaves the stored value untouched — the update endpoint merges rather than
       * replaces — so a blank box now means "leave it alone" instead of "set it to nothing".
       */
      const timings = Object.fromEntries(
        QUIZ_TABS.filter((row) => hasParts(timingPartsToSave[row.key])).map((row) => [
          row.key,
          partsToMs(timingPartsToSave[row.key])
        ])
      );
      const body = orderPayload(orderToSave);
      if (Object.keys(timings).length) {
        body.timings = timings;
        // What the admin typed, so it loads back unchanged rather than re-split into days first.
        // Numbers, so a box left blank beside a filled one reads back as 0 rather than as "unset";
        // Save has already rejected anything that isn't digits.
        body.timingsInput = Object.fromEntries(
          Object.keys(timings).map((key) => [
            key,
            Object.fromEntries(
              DURATION_FIELDS.map((f) => [f.key, Number(timingPartsToSave[key]?.[f.key]) || 0])
            )
          ])
        );
      }
      await Api.put('/admin/pollQuizConfig/update', body);
      fetchConfig();
    } catch (error) {
      console.error('Error updating poll/quiz configuration:', error);
      alert('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persistConfig(order, timingParts);

  const isDigitsOnly = (v) => v === '' || v == null || /^\d+$/.test(v);

  const handleSaveClick = () => {
    const invalid = QUIZ_TABS.some((row) =>
      Object.values(timingParts[row.key] ?? {}).some((v) => !isDigitsOnly(v))
    );
    if (invalid) {
      setTimingsError('Invalid Input');
      return;
    }
    setTimingsError(null);
    setShowSaveConfirm(true);
  };

  if (loading) {
    return (
      <Col wid="100%" ht="100vh" centerAll noFlex>
        <Spinner2 />
      </Col>
    );
  }

  return (
    <Col start noFlex wid="100%" pad="24px" gap="24px">
      {/*
        Two panels side by side, half the width each. Plain divs rather than the shared Row/Col,
        because those carry their own flex defaults and `Section` hardcodes `wid="100%"` — a wrapper
        that owns nothing but its width is the least surprising way to halve it without changing a
        component four other tabs render. `flexWrap` with a floor of 320px so they stack rather than
        crush on a narrow window, and `alignItems: stretch` so both panels share the taller height.
      */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          gap: '20px',
          width: '100%'
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex' }}>
          <Section
            title="What fans see on Team Channel"
            description="Poll and Quick Quiz share one space on the Team Channel. The first one available wins it."
          >
            <ClaimOrderEditor claims={order} onChange={setOrder} />
          </Section>
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex' }}>
          <Section title="Quiz Visibility">
            <Row gap="8px" marg="0 0 16px" wid="100%">
              {QUIZ_TABS.map((t) => {
                const on = t.key === activeTab;
                return (
                  <Clickable key={t.key} onClick={() => setActiveTab(t.key)}>
                    <Text
                      b2
                      bold={on}
                      marg="0"
                      // White on the red fill; grey on the plain one. This read as unstyled before:
                      // it asked for `theme.PRIMARY`, which does not exist in theme.js, so the
                      // selected tab got no background and its white text vanished into the panel.
                      col={on ? theme.PRIMARY_WHITE : theme.GREY_60}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '8px',
                        whiteSpace: 'nowrap',
                        backgroundColor: on ? theme.SECONDARY : theme.PRIMARY_WHITE,
                        border: `1px solid ${on ? theme.SECONDARY : theme.GREY_20}`
                      }}
                    >
                      {t.tab}
                    </Text>
                  </Clickable>
                );
              })}
            </Row>

            {/* Keyed on the tab so the inputs remount when it changes, rather than one tab's
                keystroke landing on the other's field during the re-render. */}
            <Col key={active.key} gap="10px" wid="100%" noFlex style={card}>
              <Text b2 bold marg="0">
                {active.label}
              </Text>

              <Row gap="8px" style={{ flexShrink: 0 }}>
                {DURATION_FIELDS.map((field) => (
                  <Col key={field.key} centerAll noFlex>
                    <DurationDigitInput
                      // type="number" was letting text a browser can't parse as a number (e.g.
                      // "++3") through unnoticed: for that input, `.value` silently reports "" while
                      // the field still visibly shows what was typed, so this handler never actually
                      // saw it. Plain text means `.value` always matches what's on screen.
                      type="text"
                      inputMode="numeric"
                      value={timingParts[active.key]?.[field.key] ?? ''}
                      onChange={(e) => {
                        if (e.target.value.length > 4) return;
                        setTimingsError(null);
                        setTimingParts((prev) => ({
                          ...prev,
                          [active.key]: { ...prev[active.key], [field.key]: e.target.value }
                        }));
                      }}
                    />
                    <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '10px' }}>
                      {field.label}
                    </Text>
                  </Col>
                ))}
              </Row>

              {timingsError && (
                <Text b2 col={theme.SECONDARY || '#c00'} marg="0">
                  {timingsError}
                </Text>
              )}

              <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11px' }}>
                {active.desc}
              </Text>
            </Col>
          </Section>
        </div>
      </div>

      {problems.length > 0 && (
        // Unreachable from this UI now that the tree only reorders a fixed set of four options, so
        // anything appearing here is a bug in this screen rather than admin error. Kept because Save
        // is disabled off it, and a disabled button with no explanation is worse than the dead code.
        <Col gap="4px" wid="100%">
          {problems.map((problem) => (
            <Text key={problem} c1 col={theme.ERROR || '#c00'} marg="0">
              {problem}
            </Text>
          ))}
        </Col>
      )}

      <Row marg="0 0 48px" centerAll gap="12px">
        <Button text="Reset to defaults" onClick={() => setShowResetConfirm(true)} wid="200px" />
        <Button
          text={saving ? 'Updating...' : 'Save Changes'}
          onClick={handleSaveClick}
          bg={theme.PRIMARY}
          loading={saving}
          disabled={problems.length > 0 || !isDirty}
          wid="200px"
        />
      </Row>

      {showResetConfirm && (
        <Alert
          // Reset saves immediately once confirmed, rather than only staging the defaults on
          // screen — so Save Changes is correctly disabled right after, instead of asking the
          // admin to confirm the exact same discard a second time via a now-enabled Save button.
          text="Reset the Poll & Quiz Settings to default?"
          acceptText="Reset"
          refuseText="Cancel"
          accept={() => {
            const defaultOrder = [...DEFAULT_ORDER];
            const defaultTimingParts = Object.fromEntries(
              QUIZ_TABS.map((row) => [row.key, msToParts(row.defaultMs)])
            );
            setOrder(defaultOrder);
            setTimingParts(defaultTimingParts);
            setShowResetConfirm(false);
            persistConfig(defaultOrder, defaultTimingParts);
          }}
          refuse={() => setShowResetConfirm(false)}
        />
      )}

      {showSaveConfirm && (
        <Alert
          text="Save these changes to the Poll & Quiz settings?"
          acceptText="Save"
          refuseText="Cancel"
          accept={() => {
            setShowSaveConfirm(false);
            handleSave();
          }}
          refuse={() => setShowSaveConfirm(false)}
        />
      )}
    </Col>
  );
};

export default PollQuizConfigTab;
